import type { HubConnection } from "@microsoft/signalr";

export interface RealtimeOptions {
  baseUrl: string;
  hubPath?: string;
  getToken: () => string | null;
  /** A token refreshed first when it is about to expire; the hub closes a connection whose token expired, and reconnects with this. */
  getFreshToken?: () => Promise<string | null>;
}

export type Unsubscribe = () => void;

const START_RETRY_MS = [1000, 3000, 10000, 30000];

/** Events the hub publishes to case groups (see IRealtimePublisher call sites in the backend). */
const KNOWN_EVENTS = [
  "agent.step", "case.updated", "case.update", "case.transitioned", "case.submit.refused",
  "document.uploaded", "document.progress", "document.updated", "document.downloaded",
  "proposal.created", "proposal.updated", "proposal.approved", "proposal.rejected", "proposal.edited",
  "session.revoked",
] as const;

// HubConnectionState is a string enum in @microsoft/signalr; compared by value so the library itself loads on first use.
const CONNECTED = "Connected";
const DISCONNECTED = "Disconnected";

type Handler = (payload: unknown) => void;

/**
 * Thin wrapper over a SignalR hub connection used for agent-run streaming, case updates and notification pushes. It connects on first
 * subscribe, and the SignalR library itself loads then (dynamic import), so it is never part of an app's first paint. Handlers are kept
 * here and fanned out from one dispatcher per event, so `on()` stays synchronous while the connection is still being built.
 */
export class RealtimeClient {
  private connection: HubConnection | null = null;
  private building: Promise<HubConnection> | null = null;
  private starting: Promise<void> | null = null;
  private readonly groups = new Set<string>();
  private readonly handlers = new Map<string, Set<Handler>>();
  private readonly dispatched = new Set<string>();

  constructor(private readonly options: RealtimeOptions) {}

  private dispatcher(event: string): Handler {
    return (payload: unknown) => {
      for (const h of [...(this.handlers.get(event) ?? [])]) h(payload);
    };
  }

  /** Registers the fan-out for an event on the live connection, once. */
  private bind(conn: HubConnection, event: string): void {
    if (this.dispatched.has(event)) return;
    this.dispatched.add(event);
    conn.on(event, this.dispatcher(event));
  }

  private async build(): Promise<HubConnection> {
    if (this.connection) return this.connection;
    if (!this.building) {
      this.building = import("@microsoft/signalr").then(({ HubConnectionBuilder, LogLevel }) => {
        const conn = new HubConnectionBuilder()
          .withUrl(`${this.options.baseUrl}${this.options.hubPath ?? "/hubs/ncp"}`, {
            accessTokenFactory: async () => (this.options.getFreshToken ? await this.options.getFreshToken() : this.options.getToken()) ?? "",
          })
          .withAutomaticReconnect([0, 1000, 3000, 5000, 10000])
          .configureLogging(LogLevel.Warning)
          .build();
        conn.onreconnected(() => {
          for (const g of this.groups) void conn.invoke("JoinGroup", g).catch(() => undefined);
        });
        // The hub closes a connection when its access token expires (15 min); start it again with a fresh token while a session exists.
        conn.onclose(() => {
          if (conn !== this.connection || !this.options.getToken()) return;
          setTimeout(() => {
            if (conn === this.connection && this.options.getToken()) void this.ensureConnected().catch(() => undefined);
          }, 1500);
        });
        // Case groups broadcast every event to every member; a no-op handler per known event keeps the SignalR client from warning
        // about the ones a given screen does not subscribe to.
        for (const event of KNOWN_EVENTS) conn.on(event, () => undefined);
        this.dispatched.clear();
        for (const event of this.handlers.keys()) this.bind(conn, event);
        this.connection = conn;
        return conn;
      }).finally(() => {
        this.building = null;
      });
    }
    return this.building;
  }

  async ensureConnected(): Promise<HubConnection> {
    const conn = await this.build();
    if (conn.state === CONNECTED) return conn;
    if (!this.starting) {
      this.starting = this.start(conn).finally(() => {
        this.starting = null;
      });
    }
    await this.starting;
    return conn;
  }

  /** SignalR reconnects by itself only a connection that was up, so a first start that fails or is refused (429) is retried here. */
  private async start(conn: HubConnection): Promise<void> {
    for (let attempt = 0; ; attempt++) {
      try {
        if (conn.state === DISCONNECTED) await conn.start();
      } catch (e) {
        if (attempt >= START_RETRY_MS.length || conn !== this.connection) throw e;
      }
      if (conn.state === CONNECTED) return;
      if (attempt >= START_RETRY_MS.length || conn !== this.connection) throw new Error("The realtime connection could not be started.");
      await new Promise((resolve) => setTimeout(resolve, START_RETRY_MS[attempt]));
    }
  }

  async joinGroup(group: string): Promise<void> {
    this.groups.add(group);
    const conn = await this.ensureConnected();
    if (this.groups.has(group)) await conn.invoke("JoinGroup", group);
  }

  async leaveGroup(group: string): Promise<void> {
    this.groups.delete(group);
    if (this.connection?.state === CONNECTED) {
      await this.connection.invoke("LeaveGroup", group).catch(() => undefined);
    }
  }

  on<T>(event: string, handler: (payload: T) => void): Unsubscribe {
    let set = this.handlers.get(event);
    if (!set) this.handlers.set(event, (set = new Set()));
    const h = handler as Handler;
    set.add(h);
    if (this.connection) this.bind(this.connection, event);
    void this.ensureConnected().catch(() => undefined);
    return () => {
      this.handlers.get(event)?.delete(h);
    };
  }

  async stop(): Promise<void> {
    const conn = this.connection;
    this.connection = null;
    this.dispatched.clear();
    await conn?.stop();
  }
}
